import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { FileText, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import FormularyDocumentsContent from './FormularyDocumentsContent';
import MedicalDocumentsContent from './MedicalDocumentsContent';
import { ToastProvider } from '../../../components/ui/toast';

/**
 * DocumentsTab - Main Documents tab component
 * Lists indications as collapsible cards with Formulary/Medical document tabs
 */
const DocumentsTab = ({ 
  indications, 
  productId, 
  formularyId, 
  indicationsLoading,
  cachedFormularyDocuments,
  cachedMedicalDocuments,
  onDocumentsUpdate
}) => {
  const [expandedIndications, setExpandedIndications] = useState({});

  const toggleIndication = (indicationId) => {
    setExpandedIndications((prev) => ({
      ...prev,
      [indicationId]: !prev[indicationId],
    }));
  };

  if (indicationsLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-3 sm:px-4">
        <Loader2 className="h-8 w-8 sm:h-10 sm:w-10 text-primary animate-spin mb-3 sm:mb-4" />
        <p className="text-xs sm:text-sm font-medium text-foreground">
          Loading documents...
        </p>
      </div>
    );
  }

  if (!indications || indications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-center px-4">
        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-muted flex items-center justify-center mb-3 sm:mb-4">
          <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
        </div>
        <h3 className="text-base font-semibold mb-2">No Indication Information</h3>
        <p className="text-sm text-muted-foreground max-w-md">
          Indication details are not provided for this drug. Documents are organized by indication and cannot be displayed.
        </p>
      </div>
    );
  }

  return (
    <ToastProvider>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div>
          <h3 className="text-base sm:text-lg font-semibold">Documents by Indication</h3>
          <p className="text-xs sm:text-sm font-normal text-muted-foreground mt-1">
            View formulary and medical documents for each approved indication
          </p>
        </div>

        {/* Indication Cards */}
        {indications.map((indication) => (
        <Collapsible
          key={indication.IndicationId}
          open={expandedIndications[indication.IndicationId]}
          onOpenChange={() => toggleIndication(indication.IndicationId)}
        >
          <Card className="border-2 shadow-sm">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors p-4 sm:p-5 md:p-6">
                <div className="d-flex align-items-center justify-content-between">
                  <h3 className="text-sm sm:text-base font-semibold mb-0 line-clamp-2 min-w-0">{indication.Name}</h3>
                  {expandedIndications[indication.IndicationId] ? (
                    <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-200 shrink-0" />
                  ) : (
                    <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-200 shrink-0" />
                  )}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 pb-4 sm:pb-5 md:pb-6 px-4 sm:px-6 space-y-3 sm:space-y-4">
                <Tabs defaultValue="formulary" className="w-full">
                  <TabsList className="bg-muted flex-wrap h-auto gap-2 p-2 rounded-lg border shadow-sm">
                    <TabsTrigger value="formulary" className="text-[10px] sm:text-xs font-bold min-h-[44px] px-2 sm:px-3 transition-all duration-200 data-[state=active]:shadow-md data-[state=active]:border-b-2 data-[state=active]:border-primary">
                      <span className="hidden sm:inline">Pharmacy Benefit Documents</span>
                      <span className="sm:hidden">Pharmacy</span>
                    </TabsTrigger>
                    <TabsTrigger value="medical" className="text-[10px] sm:text-xs font-bold min-h-[44px] px-2 sm:px-3 transition-all duration-200 data-[state=active]:shadow-md data-[state=active]:border-b-2 data-[state=active]:border-primary">
                      <span className="hidden sm:inline">Medical Benefit Documents</span>
                      <span className="sm:hidden">Medical</span>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="formulary" className="mt-4">
                    <FormularyDocumentsContent
                      indicationId={indication.IndicationId}
                      productId={productId}
                      formularyId={formularyId}
                      cachedData={cachedFormularyDocuments?.[indication.IndicationId]}
                      onCacheUpdate={(data) => onDocumentsUpdate(indication.IndicationId, 'formulary', data)}
                    />
                  </TabsContent>

                  <TabsContent value="medical" className="mt-4">
                    <MedicalDocumentsContent
                      indicationId={indication.IndicationId}
                      productId={productId}
                      formularyId={formularyId}
                      cachedData={cachedMedicalDocuments?.[indication.IndicationId]}
                      onCacheUpdate={(data) => onDocumentsUpdate(indication.IndicationId, 'medical', data)}
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      ))}
      </div>
    </ToastProvider>
  );
};

export default DocumentsTab;
